import { detectPitch, hzToNoteInfo, PitchResult } from './pitchDetection';

export interface AudioEngineCallbacks {
  onPitch: (result: PitchResult | null) => void;
  onSpectrum: (magnitudes: Float32Array) => void;
  onTimeDomain: (data: Float32Array) => void;
  /** High resolution spectrum (FFT_SIZE_HI). ~3 Hz bins: slow but can tell adjacent low strings apart. */
  onSpectrumHi?: (magnitudes: Float32Array) => void;
}

export interface InputOptions {
  /** Specific input device (from listInputs). Omit for the system default. */
  deviceId?: string;
  /** true = voice cleanup (echo + noise suppression). false = raw instrument signal. */
  processing?: boolean;
  /**
   * Which channel of a multi-channel input to analyse (0 = left / input 1,
   * 1 = right / input 2). Omit to analyse the mono mix. An audio interface
   * exposes its inputs as channels of one device, so voice on input 1 and
   * guitar on input 2 are two engines on the same device with channel 0 and 1.
   */
  channel?: number;
}

// EMA alpha: higher = faster tracking, more jitter. Lower = smoother, more lag.
// 0.45 tracks vibrato (~5Hz) well while suppressing single-frame noise.
const EMA_ALPHA = 0.45;
const HOLD_FRAMES = 10;

export class AudioEngine {
  private context: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | MediaElementAudioSourceNode | null = null;
  private stream: MediaStream | null = null;
  /** Set when the engine is listening to a song file instead of a microphone. */
  private songEl: HTMLAudioElement | null = null;
  private animFrame: number | null = null;
  private callbacks: AudioEngineCallbacks;

  private smoothedHz = 0;
  private hasSmoothed = false;
  private holdFrames = 0;
  private lastPitch: PitchResult | null = null;

  private recorder: MediaRecorder | null = null;
  private recChunks: Blob[] = [];

  /** Human-readable name of the input actually opened (e.g. "Scarlett 4i4 USB"). */
  inputLabel = '';
  /** Channels the opened track reports (1 = mono mic, 2 = stereo pair on an interface). */
  channelCount = 1;
  private splitter: ChannelSplitterNode | null = null;

  readonly FFT_SIZE = 4096;
  // Chord / pitch-class analysis needs finer bins than the live spectrogram:
  // at 4096 a bin is ~11 Hz, which merges B2 and D3 (adjacent strings in an
  // open G). 16384 gives ~3 Hz bins over a ~340 ms window.
  readonly FFT_SIZE_HI = 16384;
  private analyserHi: AnalyserNode | null = null;

  constructor(callbacks: AudioEngineCallbacks) {
    this.callbacks = callbacks;
  }

  /** Audio inputs the browser can see. Labels are empty until mic permission has been granted once. */
  static async listInputs(): Promise<MediaDeviceInfo[]> {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    const all = await navigator.mediaDevices.enumerateDevices();
    return all.filter(d => d.kind === 'audioinput');
  }

  async start(opts: InputOptions = {}) {
    const processing = opts.processing ?? true;
    const audio: MediaTrackConstraints = {
      // Voice mode (processing on): echoCancellation removes speaker output (piano
      // notes) from mic input so the piano can play while the voice is tracked;
      // noiseSuppression damps fan/AC hiss.
      // Instrument mode (processing off): raw signal. Browser voice processing
      // eats guitar transients and low strings, and an audio interface has no
      // speaker bleed to cancel.
      // autoGainControl is always off so dynamics stay intact and the YIN
      // clarity score isn't muddied by gain riding.
      echoCancellation: processing,
      noiseSuppression: processing,
      autoGainControl: false,
    };
    if (opts.deviceId) audio.deviceId = { exact: opts.deviceId };
    if (opts.channel !== undefined) audio.channelCount = { ideal: Math.max(2, opts.channel + 1) };
    this.stream = await navigator.mediaDevices.getUserMedia({ audio });
    const track = this.stream.getAudioTracks()[0];
    this.inputLabel = track?.label ?? '';
    this.channelCount = (track?.getSettings() as { channelCount?: number } | undefined)?.channelCount ?? 1;

    this.context = new AudioContext();
    this.makeAnalysers();

    this.source = this.context.createMediaStreamSource(this.stream);
    if (opts.channel !== undefined && this.source.channelCount > 1) {
      // Pick one channel of a multi-channel input
      const n = Math.max(2, this.source.channelCount);
      const ch = Math.min(opts.channel, n - 1);
      this.splitter = this.context.createChannelSplitter(n);
      this.source.connect(this.splitter);
      this.splitter.connect(this.analyser!, ch);
      this.splitter.connect(this.analyserHi!, ch);
    } else {
      this.source.connect(this.analyser!);
      this.source.connect(this.analyserHi!);
    }

    this.wakeOnTouch();
    this.loop();
  }

  /**
   * Listen to a song file instead of a microphone, for anyone without one.
   * The song plays aloud on a loop and feeds the same analysis a mic would;
   * it also becomes the stream Rec captures.
   */
  async startFromFile(file: File) {
    const el = new Audio(URL.createObjectURL(file));
    el.loop = true;
    this.songEl = el;
    this.inputLabel = file.name.replace(/\.[^.]+$/, '');
    this.channelCount = 1;

    this.context = new AudioContext();
    this.makeAnalysers();
    this.source = this.context.createMediaElementSource(el);
    this.source.connect(this.analyser!);
    this.source.connect(this.analyserHi!);
    this.source.connect(this.context.destination);
    const out = this.context.createMediaStreamDestination();
    this.source.connect(out);
    this.stream = out.stream;

    this.wakeOnTouch();
    this.context.resume().catch(() => {});
    el.play().catch(() => {});
    this.loop();
  }

  private makeAnalysers() {
    const ctx = this.context!;
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = this.FFT_SIZE;
    this.analyser.smoothingTimeConstant = 0.5;

    this.analyserHi = ctx.createAnalyser();
    this.analyserHi.fftSize = this.FFT_SIZE_HI;
    this.analyserHi.smoothingTimeConstant = 0.6;
  }

  // Phones and some browsers hold a new AudioContext (and a song) until the
  // page is touched. Wake them on the first touch or key so the app still
  // opens listening.
  private wakeOnTouch() {
    const ctx = this.context!;
    const el = this.songEl;
    if (ctx.state !== 'suspended' && (!el || !el.paused)) return;
    const wake = () => {
      ctx.resume().catch(() => {});
      if (el?.paused) el.play().catch(() => {});
      window.removeEventListener('pointerdown', wake, true);
      window.removeEventListener('keydown', wake, true);
    };
    window.addEventListener('pointerdown', wake, true);
    window.addEventListener('keydown', wake, true);
  }

  stop() {
    if (this.animFrame !== null) cancelAnimationFrame(this.animFrame);
    this.source?.disconnect();
    this.splitter?.disconnect();
    this.splitter = null;
    this.stream?.getTracks().forEach(t => t.stop());
    if (this.songEl) {
      this.songEl.pause();
      URL.revokeObjectURL(this.songEl.src);
      this.songEl = null;
    }
    this.context?.close();
    this.context = null;
    this.analyser = null;
    this.analyserHi = null;
    this.source = null;
    this.stream = null;
    this.smoothedHz = 0;
    this.hasSmoothed = false;
    this.holdFrames = 0;
    this.lastPitch = null;
  }

  private loop = () => {
    if (!this.analyser || !this.context) return;

    const timeDomain = new Float32Array(this.analyser.fftSize);
    this.analyser.getFloatTimeDomainData(timeDomain);

    const freqData = new Float32Array(this.analyser.frequencyBinCount);
    this.analyser.getFloatFrequencyData(freqData);

    const raw = detectPitch(timeDomain, this.context.sampleRate);

    if (raw) {
      // Snap EMA on jumps > 1 octave (e.g. speaking → whistling) so the
      // display doesn't smear through intermediate pitches for several frames
      const ratio = this.hasSmoothed ? raw.hz / this.smoothedHz : 1;
      this.smoothedHz = this.hasSmoothed && ratio < 2 && ratio > 0.5
        ? EMA_ALPHA * raw.hz + (1 - EMA_ALPHA) * this.smoothedHz
        : raw.hz; // snap directly on large jumps
      this.hasSmoothed = true;

      const info = hzToNoteInfo(this.smoothedHz);
      const result: PitchResult = { ...info, clarity: raw.clarity };

      this.lastPitch = result;
      this.holdFrames = HOLD_FRAMES;
      this.callbacks.onPitch(result);
    } else {
      if (this.holdFrames > 0) {
        this.holdFrames--;
        this.callbacks.onPitch(this.lastPitch);
      } else {
        this.hasSmoothed = false;
        this.lastPitch = null;
        this.callbacks.onPitch(null);
      }
    }

    this.callbacks.onSpectrum(freqData);
    this.callbacks.onTimeDomain(timeDomain);

    if (this.callbacks.onSpectrumHi && this.analyserHi) {
      const hi = new Float32Array(this.analyserHi.frequencyBinCount);
      this.analyserHi.getFloatFrequencyData(hi);
      this.callbacks.onSpectrumHi(hi);
    }

    this.animFrame = requestAnimationFrame(this.loop);
  };

  get sampleRate() {
    return this.context?.sampleRate ?? 44100;
  }

  get binCount() {
    return this.analyser?.frequencyBinCount ?? this.FFT_SIZE / 2;
  }

  get binCountHi() {
    return this.analyserHi?.frequencyBinCount ?? this.FFT_SIZE_HI / 2;
  }

  // ── Recording (MediaRecorder over the live mic stream) ──
  startRecording(): boolean {
    if (!this.stream || this.recorder?.state === 'recording') return false;
    this.recChunks = [];
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm';
    this.recorder = new MediaRecorder(this.stream, { mimeType });
    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.recChunks.push(e.data);
    };
    this.recorder.start();
    return true;
  }

  stopRecording(): Promise<Blob | null> {
    return new Promise((resolve) => {
      const rec = this.recorder;
      if (!rec || rec.state === 'inactive') return resolve(null);
      rec.onstop = () => {
        const type = rec.mimeType || 'audio/webm';
        const blob = new Blob(this.recChunks, { type });
        this.recChunks = [];
        this.recorder = null;
        resolve(blob);
      };
      rec.stop();
    });
  }

  get isRecording(): boolean {
    return this.recorder?.state === 'recording';
  }
}
